import { CommonModule } from '@angular/common';
import { Component, ChangeDetectionStrategy, Input } from '@angular/core';

import { Icon, IconFillColor } from '@design-system/components/atoms/icon/entities/icon.types';
import {
  TagMemberSize, TagMemberSliced, TagMemberStrikethrough, TagMemberStyle, TagMemberType
} from '@design-system/components/molecules/tag-member/entities/tag-member.types';
import {
  TypographyColor, TypographyFontFamily, TypographyFontSize
} from '@design-system/components/atoms/typography/entities/typography.types';
import { isEmpty } from '@app/util/util';
import { TypographyDsComponent } from '@design-system/components/atoms/typography/typography.component';
import { IconDsComponent } from '@design-system/components/atoms/icon/icon.component';

@Component({
  selector: 'app-ds-tag-member',
  templateUrl: './tag-member.component.html',
  styleUrls: ['./tag-member.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    TypographyDsComponent,
    IconDsComponent,
  ]
})
export class TagMemberDsComponent {

  @Input() size = TagMemberSize.RG;
  @Input() type = TagMemberType.Rappi;
  @Input() style = TagMemberStyle.Member;
  @Input() label: string;
  @Input() strikethrough: TagMemberStrikethrough;
  @Input() sliced: TagMemberSliced;
  @Input() withoutTagBorder: boolean;

  public readonly strikethroughDecoration = 'line-through';
  public readonly strikethroughColor = TypographyColor.Secondary;
  public readonly labelFontFamily = TypographyFontFamily.SemiBold;

  constructor() { }

  get showStrikethrough(): boolean {
    return !!this.strikethrough?.Value && this.style === TagMemberStyle.Strikethrough;
  }

  get tagStyle(): string {
    return this.showStrikethrough ? (
      this.strikethrough?.Align ? `${this.strikethrough?.Align}-${this.style}` : `left-${this.style}`
    ) : this.style === TagMemberStyle.Member && !isEmpty(this.sliced) ? `${this.style}-sliced-${this.sliced}` : this.style;
  }

  get showIcon(): boolean {
    const isEnabled = this.type !== TagMemberType.Rappi || (
      this.type === TagMemberType.Rappi && this.style === TagMemberStyle.Member && isEmpty(this.sliced)
    );
    return this.showStrikethrough ? this.strikethrough?.ShowIcon && isEnabled : isEnabled;
  }

  get taggedStyle(): string {
    const modifier = 'tagged';
    const tagged = this.type === TagMemberType.Rappi ? (
      this.showStrikethrough || this.style === TagMemberStyle.Member && this.sliced === 'vertical-top' ?
        `${modifier}-simplified` : `${modifier}-${this.type}`
    ) : (
      this.showStrikethrough && !this.strikethrough?.ShowIcon ? `${modifier}-simplified` : `${modifier}-${this.type}`
    );
    return this.withoutTagBorder ? `${tagged}-without-border` : tagged;
  }

  get icon(): Icon {
    switch (this.type) {
      case TagMemberType.Pro:
        return Icon.Prime;
      case TagMemberType.Marriott:
        return Icon.MarriottBonvoyFlat;
      default:
        return Icon.MustacheFlat;
    }
  }

  get iconFillColor(): IconFillColor {
    switch (this.type) {
      case TagMemberType.Pay:
        return IconFillColor.Primary;
      case TagMemberType.Rappi:
        return IconFillColor.Neon;
      case TagMemberType.RappiCard:
        return IconFillColor.Light;
      default:
        return null;
    }
  }

  get iconSize(): number {
    if (this.type === TagMemberType.Marriott || this.size === TagMemberSize.XS) {
      return 14;
    } else if (this.size === TagMemberSize.SM) {
      return 16;
    } else {
      return 20;
    }
  }

  get fontSize(): TypographyFontSize {
    switch (this.size) {
      case TagMemberSize.XS:
        return TypographyFontSize.Minor;
      case TagMemberSize.SM:
        return TypographyFontSize.XXXS;
      case TagMemberSize.RG:
        return TypographyFontSize.XXS;
      default:
        return TypographyFontSize.XS;
    }
  }

  get labelColor(): TypographyColor {
    if (
      (this.type === TagMemberType.Marriott) ||
      (this.type === TagMemberType.Rappi && this.style === TagMemberStyle.Member && isEmpty(this.sliced)) ||
      (this.type === TagMemberType.Pay && this.style === TagMemberStyle.Member && isEmpty(this.sliced)) ||
      (this.type === TagMemberType.RappiCard && this.showStrikethrough && !this.strikethrough?.ShowIcon)
    ) {
      return TypographyColor.Primary;
    } else if (this.type === TagMemberType.Pro) {
      return TypographyColor.PrimeBranding;
    } else if (this.type === TagMemberType.Rappi) {
      return TypographyColor.Neon;
    } else if (this.type === TagMemberType.Pay) {
      return TypographyColor.Pay;
    } else if (this.type === TagMemberType.RappiCard) {
      return TypographyColor.Invert;
    } else {
      return null;
    }
  }

}
